import "./queueFleet.css";
import { api } from "../api";
import { emptyBlock, errorBanner, loadingBlock, pill, tableRegion, tag } from "./shared/blocks";
import { createLifecycle, isAbort } from "./shared/lifecycle";
import { clientHref, h, safeMessage, tidy } from "./shared/safe";
import { FLEET_MAX_LIMIT, FLEET_PAGE_SIZE, FLEET_STATUS_FILTERS, FLEET_STATUS_LABELS, filterClients, fleetQuery, fleetStatusTone, needsAttention, parseClients, parseFleetFilter, parsePage, rangeLabel, sortByAttention, type FleetClient, type FleetPage, type FleetStatusFilter } from "./fleetModel";

// Fleet: the client list. With a server that supports search and paging (the response carries `page`) the list is searched, filtered by
// status and paged ON THE SERVER, 25 at a time, so it stays usable at 60+ clients; only the current page is held and rendered. A server that
// ignores those parameters (an older one) gets the previous behaviour: everything loaded once, filtered here, attention first.
// "Needs attention" is derived from counters the server cannot filter on, so that one view reads the whole list (200 per request) and pages it here.
//
// (Original note) Every client, attention first. Built with h(): names, slugs, locations, statuses and modes from the
// API are text nodes, the search index lives in memory (it used to be a data-search attribute assembled from
// the same strings), and every link's id is percent-encoded.

const SEARCH_THRESHOLD = 8; // the toolbar appears once the list is long enough to need it (unchanged behaviour)
// ...and ALWAYS while a filter is active: a filter restored from the session must never hide clients without a visible way to see and clear it.

// Filter context survives opening a client and coming back (sessionStorage, with an in-memory fallback
// when storage is blocked). Values read back are validated like any other untrusted input.
const FILTER_KEY = "cloudpush_fleet_filters";
let memory: { search: string; status: FleetStatusFilter } = { search: "", status: "all" };

function loadFilters(): { search: string; status: FleetStatusFilter } {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(FILTER_KEY);
  } catch {
    return memory; // storage blocked: the in-memory copy is the only record there is
  }
  try {
    const parsed: unknown = JSON.parse(stored ?? "null");
    if (typeof parsed === "object" && parsed !== null) {
      const p = parsed as Record<string, unknown>;
      return { search: typeof p.search === "string" ? p.search.slice(0, 200) : "", status: parseFleetFilter(p.status) };
    }
  } catch {
    /* corrupt value: ignore it rather than let an unreadable filter hide clients */
  }
  return { search: "", status: "all" };
}
function saveFilters(search: string, status: FleetStatusFilter) {
  memory = { search, status };
  try {
    sessionStorage.setItem(FILTER_KEY, JSON.stringify(memory));
  } catch {
    /* storage blocked: the in-memory copy still covers navigation within this session */
  }
}

export async function fleetView(root: HTMLElement, presetStatus?: FleetStatusFilter): Promise<void> {
  const shell = h("div", { class: "qf qf-fleet" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);

  let clients: FleetClient[] = [];
  /** null until the first reply says whether the server pages; true = server search/paging, false = everything is loaded and filtered here. */
  let serverPaged: boolean | null = null;
  let page: FleetPage | null = null;
  let offset = 0;
  /** Needs-attention view: the whole list was read, so paging is local. */
  let allRead = false;
  let hasData = false;
  let dropped = 0;
  let loading = false;
  let loadError: string | null = null;
  const restored = loadFilters();
  let search = restored.search;
  let status = presetStatus ?? restored.status;

  const content = h("div", { class: "qf-content" });
  shell.append(h("div", { class: "topbar" }, h("div", null, h("h1", null, "Clients"), h("p", { class: "muted" }, "Find and manage every client from one workspace.")), h("div", { class: "btn-group" }, h("a", { class: "btn secondary", href: "#/clients/import" }, "Import clients"), h("a", { class: "btn", href: "#/clients/new" }, "Add client"))), content);

  // The toolbar is created once so typing keeps focus; only the results beneath it are rebuilt.
  const searchInput = h("input", { id: "fleet-search", type: "search", value: search, placeholder: "Name, slug or location", autocomplete: "off", "aria-controls": "fleet-results" });
  const statusSelect = h("select", { id: "fleet-status", "aria-controls": "fleet-results" }, ...FLEET_STATUS_FILTERS.map((f) => h("option", { value: f }, FLEET_STATUS_LABELS[f])));
  statusSelect.value = status;
  const countNote = h("p", { class: "qf-summary", role: "status" });
  const results = h("div", { id: "fleet-results" });
  const clearFilters = h("button", { type: "button", class: "secondary", on: { click: () => setFilters("", "all") } }, "Clear filters");
  const toolbar = h(
    "div",
    { class: "qf-toolbar", role: "search", "aria-label": "Filter clients" },
    h("div", { class: "qf-grow" }, h("label", { class: "qf-label", for: "fleet-search" }, "Search clients"), searchInput),
    h("div", null, h("label", { class: "qf-label", for: "fleet-status" }, "Status"), statusSelect),
    h("div", { class: "qf-toolbar__clear" }, clearFilters),
  );

  function attentionCell(c: FleetClient): HTMLElement {
    const chips = [c.reconnectNeeded > 0 ? pill(`${c.reconnectNeeded} reconnect`, "bad") : null, c.reviewWaiting > 0 ? pill(`${c.reviewWaiting} review`, "warn") : null].filter((x): x is HTMLElement => x !== null);
    return chips.length ? h("span", { class: "btn-group" }, ...chips) : h("span", { class: "muted" }, "—");
  }

  function row(c: FleetClient): HTMLElement {
    const partial = c.sitesTotal > 0 && c.sitesLive < c.sitesTotal;
    const sites = `${c.sitesLive}/${c.sitesTotal} live`;
    const location = [c.city, c.state].filter(Boolean).join(", ");
    return h(
      "tr",
      null,
      h(
        "td",
        null,
        c.id !== null ? h("a", { class: "qf-business qf-wrap", href: clientHref(c.id) }, tidy(c.name, 300)) : h("span", { class: "qf-business qf-wrap" }, tidy(c.name, 300)),
        h("div", { class: "muted qf-wrap" }, tidy(c.slug, 80)),
      ),
      h("td", { dataset: { priority: "mid" }, class: "qf-wrap" }, location ? tidy(location, 80) : "—"),
      h("td", null, pill(tidy(c.status, 24), fleetStatusTone(c.status))),
      h("td", { dataset: { priority: "low" } }, c.mode ? tag(tidy(c.mode, 24)) : "—"),
      h("td", null, partial ? pill(sites, "warn") : sites),
      h("td", null, attentionCell(c)),
      h("td", null, c.id !== null ? h("a", { class: "qf-open", href: clientHref(c.id), "aria-label": `Open ${tidy(c.name, 80)}` }, "Open →") : ""),
    );
  }

  const filtersActive = () => search.trim() !== "" || status !== "all";
  const total = () => (serverPaged && page && !allRead ? page.total : clients.length);
  const toolbarShown = () => total() > SEARCH_THRESHOLD || filtersActive();

  /** What the current view lists, and its place in the whole result. */
  function view(): { rows: FleetClient[]; from: number; of: number } {
    if (!serverPaged) {
      const rows = sortByAttention(filterClients(clients, search, status));
      return { rows, from: 0, of: rows.length };
    }
    if (allRead) {
      const matching = filterClients(clients, search, status);
      return { rows: matching.slice(offset, offset + FLEET_PAGE_SIZE), from: offset, of: matching.length };
    }
    return { rows: clients, from: page?.offset ?? 0, of: page?.total ?? clients.length };
  }

  const pager = h("nav", { class: "qf-pager", "aria-label": "Client pages" });
  const prevBtn = h("button", { type: "button", class: "secondary", id: "fleet-prev", on: { click: () => go(offset - FLEET_PAGE_SIZE) } }, "← Previous");
  const nextBtn = h("button", { type: "button", class: "secondary", id: "fleet-next", on: { click: () => go(offset + FLEET_PAGE_SIZE) } }, "Next →");
  const pagerNote = h("span", { class: "qf-pager__note", role: "status", "aria-live": "polite", id: "fleet-range" });
  pager.append(prevBtn, pagerNote, nextBtn);

  function paintPager(from: number, shown: number, of: number) {
    // aria-disabled (not disabled): a button that becomes disabled would drop keyboard focus out of the pager.
    const first = from <= 0;
    const last = from + shown >= of;
    prevBtn.setAttribute("aria-disabled", String(first));
    nextBtn.setAttribute("aria-disabled", String(last));
    pagerNote.textContent = `Showing ${rangeLabel(from, shown, of)}`;
    pager.hidden = !serverPaged || of <= FLEET_PAGE_SIZE;
  }

  function paintResults() {
    const v = view();
    const visible = v.rows;
    const shown = toolbarShown();
    clearFilters.hidden = !filtersActive();
    countNote.textContent = shown
      ? serverPaged
        ? `${v.of} ${v.of === 1 ? "client matches" : "clients match"}.${dropped ? ` ${dropped} unreadable ${dropped === 1 ? "row was" : "rows were"} skipped.` : ""}`
        : `Showing ${visible.length} of ${clients.length} ${clients.length === 1 ? "client" : "clients"}.${dropped ? ` ${dropped} unreadable ${dropped === 1 ? "row was" : "rows were"} skipped.` : ""}`
      : dropped
        ? `${dropped} unreadable ${dropped === 1 ? "row was" : "rows were"} skipped.`
        : "";
    paintPager(v.from, visible.length, v.of);
    if (visible.length === 0) {
      // The toolbar (always visible while a filter is active) owns the one "Clear filters" button.
      results.replaceChildren(emptyBlock("No client matches those filters", "Try a different search or status, or use Clear filters above."));
      return;
    }
    const th = (label: string, priority?: string) => h("th", { scope: "col", dataset: { priority } }, label);
    const table = h(
      "table",
      { class: "qf-table" },
      h("caption", { class: "qf-sr-only" }, serverPaged ? `Clients, ${rangeLabel(v.from, visible.length, v.of)}.` : `Clients, attention first. ${visible.length} shown.`),
      h("thead", null, h("tr", null, th("Business"), th("Location", "mid"), th("Status"), th("Mode", "low"), th("Sites"), th("Attention"), th(""))),
      h("tbody", null, ...visible.map(row)),
    );
    // Give the empty header cell a name for assistive technology.
    table.querySelector("thead th:last-child")!.append(h("span", { class: "qf-sr-only" }, "Open"));
    results.replaceChildren(tableRegion("Clients", table));
  }

  /** Filters changed: with server paging that is a new query from the first page; otherwise it is filtered here. */
  function applyFilters() {
    saveFilters(search, status);
    offset = 0;
    if (serverPaged) void load();
    else paintResults();
  }
  function setFilters(nextSearch: string, nextStatus: FleetStatusFilter) {
    search = nextSearch;
    status = nextStatus;
    searchInput.value = nextSearch;
    statusSelect.value = nextStatus;
    applyFilters();
  }
  function go(next: number) {
    if (next < 0 || !page) return;
    const last = view().of;
    if (next >= last) return;
    offset = next;
    if (serverPaged && !allRead) void load();
    else paintResults();
    // the pager keeps focus (it is not rebuilt); the results table is replaced
  }
  // Typing: the server is asked once the person pauses (or presses Enter), not for every keystroke.
  let debounce: ReturnType<typeof setTimeout> | undefined;
  searchInput.addEventListener("input", () => {
    search = searchInput.value;
    saveFilters(search, status);
    if (!serverPaged) return void paintResults();
    clearTimeout(debounce);
    debounce = setTimeout(applyFilters, 250);
  });
  searchInput.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") {
      clearTimeout(debounce);
      search = searchInput.value;
      applyFilters();
    }
  });
  statusSelect.addEventListener("change", () => {
    status = parseFleetFilter(statusSelect.value);
    applyFilters();
  });

  // The page is built once per mode (loading / error / empty / list). Within the list mode only the stats, count and results are
  // refreshed, so the search box, the status select and the pager keep keyboard focus across a search or a page change.
  let mode: "loading" | "error" | "empty" | "list" | null = null;
  const statsHost = h("div", { class: "qf-stats" });
  const errorSlot = h("div");

  function paintStats() {
    const sum = (pick: (c: FleetClient) => number) => clients.reduce((n, c) => n + pick(c), 0);
    const stat = (n: number, label: string) => h("div", { class: "qf-stat" }, h("div", { class: "qf-stat__n" }, String(n)), h("div", { class: "qf-stat__l" }, label));
    const here = serverPaged && !allRead ? " (this page)" : "";
    statsHost.replaceChildren(
      stat(serverPaged ? view().of : clients.length, serverPaged && filtersActive() ? "Clients matching" : "Clients"),
      stat(sum((c) => c.reconnectNeeded), `Reconnect needed${here}`),
      stat(sum((c) => c.reviewWaiting), `Waiting for review${here}`),
      stat(clients.filter((c) => c.status === "active").length, `Active${here}`),
    );
    errorSlot.replaceChildren(...(loadError ? [errorBanner(`Couldn't refresh: ${loadError} Showing the clients that loaded earlier.`, { retry: () => void load() })] : []));
  }

  function paint() {
    if (!hasData && loading) {
      mode = "loading";
      return void content.replaceChildren(loadingBlock("Loading clients"));
    }
    if (!hasData) {
      mode = "error";
      return void content.replaceChildren(errorBanner(loadError ?? "The clients could not be loaded.", { retry: () => void load() }));
    }
    if (clients.length === 0 && !(serverPaged && filtersActive())) {
      mode = "empty";
      return void content.replaceChildren(emptyBlock("No clients yet", "Add your first client to start building their websites.", [h("a", { class: "btn", href: "#/clients/new" }, "Add your first client"), h("a", { class: "btn secondary", href: "#/clients/import" }, "Import clients")]));
    }
    // The toolbar appears once the list is long enough (or a filter is active) and then stays: it never disappears under a person who is typing.
    const wantToolbar = toolbarShown();
    if (mode !== "list" || toolbar.isConnected !== wantToolbar) {
      mode = "list";
      content.replaceChildren(errorSlot, statsHost, ...(wantToolbar ? [toolbar] : []), countNote, results, pager);
    }
    paintStats();
    paintResults();
  }

  /** Reads the whole list, 200 per request, for the Needs-attention view (the server cannot filter on attention counters). */
  async function readAll(req: { signal: AbortSignal; current(): boolean }) {
    const all: FleetClient[] = [];
    let skipped = 0;
    let at = 0;
    for (let guard = 0; guard < 50; guard++) {
      const body = await api<unknown>(`/v1/clients${fleetQuery(search, "all", at, FLEET_MAX_LIMIT)}`, { signal: req.signal });
      const parsed = parseClients(body);
      const pg = parsePage(body);
      if (!parsed || !pg) throw new Error("The server's reply was not in the expected format.");
      all.push(...parsed.clients);
      skipped += parsed.dropped;
      if (!pg.hasMore || parsed.clients.length + parsed.dropped === 0) break;
      at += pg.limit;
    }
    return { all, skipped };
  }

  async function load(): Promise<void> {
    const req = life.begin();
    loading = true;
    if (!hasData) paint();
    try {
      if (serverPaged && status === "needs_attention") {
        const { all, skipped } = await readAll(req);
        if (!req.current()) return;
        clients = all.filter(needsAttention);
        // search was applied by the server; "needs attention" here: local list, local paging
        dropped = skipped;
        allRead = true;
        page = { total: clients.length, limit: FLEET_PAGE_SIZE, offset: 0, returned: Math.min(FLEET_PAGE_SIZE, clients.length), hasMore: clients.length > FLEET_PAGE_SIZE };
      } else {
        const body = await api<unknown>(`/v1/clients${fleetQuery(search, status, offset)}`, { signal: req.signal });
        if (!req.current()) return;
        const parsed = parseClients(body);
        if (!parsed) throw new Error("The server's reply was not in the expected format.");
        const pg = parsePage(body);
        serverPaged = pg !== null;
        allRead = false;
        page = pg;
        clients = parsed.clients;
        dropped = parsed.dropped;
        // The first reply tells whether the server pages. A restored "Needs attention" filter then needs the whole list.
        if (serverPaged && status === "needs_attention") return void load();
        // an offset past the end (the list shrank): show the last page
        if (pg && pg.returned === 0 && pg.total > 0 && offset > 0) {
          offset = Math.floor((pg.total - 1) / pg.limit) * pg.limit;
          return void load();
        }
      }
      hasData = true;
      loadError = null;
    } catch (e) {
      if (isAbort(e) || !req.current()) return;
      loadError = safeMessage((e as Error).message || "The clients could not be loaded.");
    }
    if (req.current()) {
      loading = false;
      paint();
    }
  }

  paint();
  await load();
}
